import type { FrontendSettings } from "../types";

export const DEFAULT_PRICE_CALENDAR_API_URL =
  "https://price-checker-api.cee-systems.com/v3/period-calendar";

export const DEFAULT_PRICE_CALENDAR_LENGTH_OF_STAY = [5, 7, 14];

export enum PriceCalendarSelectionMode {
  OUTBOUND = "OUTBOUND",
  RETURN = "RETURN",
}

export type PriceCalendarTier = "low" | "medium" | "expensive";

export interface PriceCalendarSettings {
  enabled: boolean;
  apiUrl: string;
  principalId: string;
}

export interface PriceCalendarOptions extends PriceCalendarSettings {
  origin: string;
  destination: string;
  cabin: string;
  maxTransfers: number;
}

export interface PriceCalendarRequest {
  apiUrl?: string;
  principalId: string;
  origin: string;
  destination: string;
  periodStart: string;
  periodEnd: string;
  cabin?: string;
  maxTransfers?: number;
  mode?: string;
  source?: string;
  lengthOfStay?: number[];
  sourceDepartureDate?: string;
}

export interface PriceCalendarRequestConfig {
  principalIdPath: string;
  config: {
    baseURL: string;
    params: Record<string, string | number>;
  };
}

export interface PriceCalendarDay {
  date: string;
  price: number;
  currency: string;
  source?: string;
  fareType?: string;
}

export type PriceCalendarDays = Record<string, PriceCalendarDay>;

export type PriceCalendarTiers = Record<string, PriceCalendarTier>;

interface PriceCalendarApiDay {
  date?: string;
  price?: number | string;
  currency?: string;
  source?: string;
  fareType?: string;
  detail?: {
    Price?: number | string;
    PriceCurrency?: string;
  };
}

const pad2 = (value: number) => String(value).padStart(2, "0");

export const toPriceCalendarDateKey = (date: Date | string): string => {
  if (typeof date === "string") {
    return date.slice(0, 10);
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
};

export const getPriceCalendarPeriod = (
  month: Date,
  numberOfMonths = 2
): { periodStart: string; periodEnd: string } => {
  const start = new Date(month.getFullYear(), month.getMonth(), 1);
  const end = new Date(
    month.getFullYear(),
    month.getMonth() + numberOfMonths,
    0
  );

  return {
    periodStart: toPriceCalendarDateKey(start),
    periodEnd: toPriceCalendarDateKey(end),
  };
};

export const getPriceCalendarInitialSelectionMode = (
  type: string,
  departureDate?: string
): PriceCalendarSelectionMode =>
  type === "returnDate" && departureDate
    ? PriceCalendarSelectionMode.RETURN
    : PriceCalendarSelectionMode.OUTBOUND;

export const getPriceCalendarSettings = (
  frontendSettings?: FrontendSettings
): PriceCalendarSettings => {
  const settings = frontendSettings?.dealerFrontendSettings;
  const localPrincipalId =
    process.env.NODE_ENV !== "production"
      ? process.env.NEXT_PUBLIC_D4_PRICE_CALENDAR_PRINCIPAL_ID?.trim() || ""
      : "";
  const principalId =
    settings?.price_calendar_principal_id?.trim() || localPrincipalId || "";
  const apiUrl =
    settings?.price_calendar_api_url?.trim() || DEFAULT_PRICE_CALENDAR_API_URL;

  return {
    enabled: Boolean(principalId),
    apiUrl,
    principalId,
  };
};

export const getPriceCalendarCabin = (flightClass: string): string => {
  switch (flightClass) {
    case "1ST":
      return "first";
    case "BUS":
      return "business";
    case "PRE":
      return "premium_economy";
    default:
      return "economy";
  }
};

export const getPriceCalendarTier = (
  day: PriceCalendarDay | undefined,
  days: PriceCalendarDays
): PriceCalendarTier | undefined => {
  if (!day) {
    return undefined;
  }

  const prices = Object.values(days)
    .map((priceDay) => priceDay.price)
    .filter((price) => Number.isFinite(price));

  if (prices.length === 0) {
    return undefined;
  }

  const min = Math.min(...prices);
  const max = Math.max(...prices);

  if (max <= min) {
    return "low";
  }

  const position = (day.price - min) / (max - min);

  if (position > 2 / 3) {
    return "expensive";
  }

  if (position > 1 / 3) {
    return "medium";
  }

  return "low";
};

export const getPriceCalendarTiers = (
  days: PriceCalendarDays
): PriceCalendarTiers =>
  Object.entries(days).reduce<PriceCalendarTiers>((tiers, [date, day]) => {
    const tier = getPriceCalendarTier(day, days);

    return tier ? { ...tiers, [date]: tier } : tiers;
  }, {});

export const formatPriceCalendarAmount = (
  day: PriceCalendarDay | undefined
): string => {
  if (!day) {
    return "";
  }

  return String(Math.round(day.price)).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    "\u202f"
  );
};

export const formatPriceCalendarPrice = (
  day: PriceCalendarDay | undefined
): string => {
  const amount = formatPriceCalendarAmount(day);

  return amount && day ? `${amount} ${day.currency}` : "";
};

const getNormalizedApiUrl = (apiUrl?: string): string =>
  (apiUrl || DEFAULT_PRICE_CALENDAR_API_URL).replace(/\/+$/, "");

const getQueryString = (params: Record<string, string | number>): string =>
  Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&");

const getSegmentCodes = (value: string): string[] =>
  value
    .split("/")
    .map((code) => code.replace(/[^a-z0-9]/gi, "").toUpperCase())
    .filter(Boolean);

const getSegments = (origin: string, destination: string): string => {
  const originCode = getSegmentCodes(origin)[0];
  const destinationCode = getSegmentCodes(destination)[0];

  return originCode && destinationCode
    ? `${originCode}-${destinationCode},${destinationCode}-${originCode}`
    : "";
};

export const getPriceCalendarRequestConfig = ({
  apiUrl,
  principalId,
  origin,
  destination,
  periodStart,
  periodEnd,
  cabin = "economy",
  maxTransfers = 2,
  mode = "mixed",
  source = "all",
  lengthOfStay = DEFAULT_PRICE_CALENDAR_LENGTH_OF_STAY,
  sourceDepartureDate,
}: PriceCalendarRequest): PriceCalendarRequestConfig | null => {
  const segments = getSegments(origin, destination);

  if (!principalId || !segments || !periodStart || !periodEnd) {
    return null;
  }

  const params: Record<string, string | number> = {
    segments,
    source,
    cabin,
    maxTransfers,
    mode,
    periodStart,
    periodEnd,
  };

  if (sourceDepartureDate) {
    params.sourceDepartureDate = sourceDepartureDate;
  } else if (lengthOfStay.length > 0) {
    params.lengthOfStay = lengthOfStay.join(",");
  }

  return {
    principalIdPath: encodeURIComponent(principalId),
    config: {
      baseURL: getNormalizedApiUrl(apiUrl),
      params,
    },
  };
};

const getNormalizedDay = (
  day: PriceCalendarApiDay
): PriceCalendarDay | null => {
  const { date } = day;
  const price = Number(day.price ?? day.detail?.Price);
  const currency = day.currency || day.detail?.PriceCurrency || "";

  if (!date || !Number.isFinite(price) || !currency) {
    return null;
  }

  return {
    date,
    price,
    currency,
    source: day.source,
    fareType: day.fareType,
  };
};

export const fetchPriceCalendar = async ({
  apiUrl,
  principalId,
  origin,
  destination,
  periodStart,
  periodEnd,
  cabin = "economy",
  maxTransfers = 2,
  mode = "mixed",
  source = "all",
  lengthOfStay = DEFAULT_PRICE_CALENDAR_LENGTH_OF_STAY,
  sourceDepartureDate,
}: PriceCalendarRequest): Promise<PriceCalendarDays> => {
  const requestConfig = getPriceCalendarRequestConfig({
    apiUrl,
    principalId,
    origin,
    destination,
    periodStart,
    periodEnd,
    cabin,
    maxTransfers,
    mode,
    source,
    lengthOfStay,
    sourceDepartureDate,
  });

  if (!requestConfig) {
    return {};
  }

  try {
    const url = `${requestConfig.config.baseURL}/${
      requestConfig.principalIdPath
    }?${getQueryString(requestConfig.config.params)}`;
    const response = await fetch(url);

    if (response.status !== 200) {
      return {};
    }

    const data = await response.json();

    if (data?.result !== "Success") {
      return {};
    }

    const days = data?.context?.days;

    if (!Array.isArray(days)) {
      return {};
    }

    return days.reduce((acc: PriceCalendarDays, day: PriceCalendarApiDay) => {
      const normalizedDay = getNormalizedDay(day);

      if (!normalizedDay) {
        return acc;
      }

      return {
        ...acc,
        [normalizedDay.date]: normalizedDay,
      };
    }, {});
  } catch (_error) {
    return {};
  }
};
